import { Link } from 'react-router-dom';
import { Building2, ArrowRight } from 'lucide-react';
import { Card, CardContent, Badge } from '@/components/ui';
import type { Bar } from '@/api/endpoints/bars';

interface BarCardProps {
  bar: Bar | { id: string; name: string; role?: string };
  showRole?: boolean;
}

export function BarCard({ bar, showRole = false }: BarCardProps) {
  const hasFullData = 'address' in bar;
  const logoUrl = hasFullData ? (bar as Bar).logoUrl : null;
  const address = hasFullData ? (bar as Bar).address : null;
  const isClaimed = hasFullData ? (bar as Bar).isClaimed : false;
  const role = 'role' in bar ? bar.role : null;

  return (
    <Link to={`/admin/bars/${bar.id}`}>
      <Card className="h-full transition-colors hover:border-accent-primary/50">
        <CardContent className="flex flex-col gap-3 p-4">
          {/* Header: Logo and Name */}
          <div className="flex items-start gap-3">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={bar.name}
                className="h-12 w-12 shrink-0 rounded-lg object-cover"
              />
            ) : (
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[var(--color-border)]">
                <Building2 className="h-6 w-6 text-[var(--color-muted)]" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-medium leading-tight">{bar.name}</p>
              {address && (
                <p className="mt-0.5 truncate text-sm text-[var(--color-muted)]">
                  {address}
                </p>
              )}
            </div>
          </div>

          {/* Footer: Badges and Arrow */}
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-1">
              {showRole && role && (
                <Badge variant={role === 'owner' ? 'default' : 'secondary'}>
                  {role === 'owner' ? 'Owner' : 'Manager'}
                </Badge>
              )}
              {isClaimed && (
                <Badge variant="success">Claimed</Badge>
              )}
            </div>
            <ArrowRight className="h-4 w-4 shrink-0 text-[var(--color-muted)]" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
